"use client";

import { useSyncExternalStore } from "react";
import { Sun, Moon, SunMoon } from "lucide-react";

type Theme = "light" | "dark";

/**
 * Manual light/dark switch (design system §1/§6). Auto (system) is the
 * default; the first click commits an explicit choice, persisted in
 * localStorage under the same "bookr-theme" key the no-flash script in the
 * root layout reads before paint. Toggling sets data-theme on <html>, which
 * overrides prefers-color-scheme both ways (globals.css).
 *
 * The live theme is an external store, not React state: the authority is the
 * `data-theme` attribute on <html>, which a script sets before React exists.
 * Subscribing to it rather than copying it into state in an effect means there
 * is no cascading render on mount, and it earns something the effect version
 * never had, which is that the icon now follows the system theme when the
 * viewer changes it while a page is open and no explicit choice has been made.
 */

const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  media.addEventListener("change", onChange);
  return () => {
    listeners.delete(onChange);
    media.removeEventListener("change", onChange);
  };
}

/** An explicit choice wins; otherwise fall back to the system preference. */
function readTheme(): Theme {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "light" || attr === "dark") return attr;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** The server cannot know the theme, so it renders the neutral icon. */
function readServerTheme(): Theme | null {
  return null;
}

export function ThemeToggle({ className }: { className?: string }) {
  const theme = useSyncExternalStore<Theme | null>(
    subscribe,
    readTheme,
    readServerTheme,
  );

  function toggle() {
    // Read the live DOM rather than the rendered value, so the toggle is
    // correct even if the system preference changed under us.
    const next: Theme = readTheme() === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("bookr-theme", next);
    } catch {
      // Private mode / storage disabled, the choice just won't persist.
    }
    for (const listener of listeners) listener();
  }

  const label =
    theme === null
      ? "Toggle theme"
      : theme === "dark"
        ? "Switch to light theme"
        : "Switch to dark theme";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-body transition-colors duration-150 hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${className ?? ""}`}
    >
      {theme === null ? (
        <SunMoon className="h-4 w-4" aria-hidden="true" strokeWidth={1.8} />
      ) : theme === "dark" ? (
        <Sun className="h-4 w-4" aria-hidden="true" strokeWidth={1.8} />
      ) : (
        <Moon className="h-4 w-4" aria-hidden="true" strokeWidth={1.8} />
      )}
    </button>
  );
}
